import { useEffect, useState } from 'react'
import {
  fetchHealth, runHubspotCheck, runMailCheck,
  type HealthReport, type HubspotCheckReport, type MailCheckReport, type Probe,
} from '../lib/api.js'

/**
 * Admin → System status (decision 173 — was "Build readiness").
 *
 * Everything on this page is LIVE — the hand-maintained Built/Next lists are
 * gone (they had gone stale twice; spec §2 and CLAUDE.md are the build
 * record). /api/health answers the whole top of the page: environment &
 * access, feature configuration (the Organisation settings that gate a
 * feature), the stage map, scheduled-job recency ("is the sweep alive") and
 * queue depths. The Mail and HubSpot deep probes stay at the bottom as
 * on-demand tools.
 *
 * ⚠️ fetchHealth() is cache-busted on every call — a cached body once showed
 * a five-hour-old sweep and read as "the sync has stopped" (2026-08-16).
 */

function agoLabel(mins: number | null): string {
  if (mins == null) return '—'
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  if (mins < 48 * 60) return `${Math.round(mins / 60)} h ago`
  return `${Math.round(mins / (24 * 60))} d ago`
}

function StatusRow({ ok, label, detail, warn = false }: { ok: boolean; label: string; detail?: string; warn?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '6px 0', borderBottom: '1px solid var(--line-soft)' }}>
      <span className={`pill tiny ${ok ? 'pill--ok' : warn ? 'pill--warn' : 'pill--stop'}`}>{ok ? 'OK' : warn ? 'attention' : 'missing'}</span>
      <strong className="small">{label}</strong>
      {detail && <span className="tiny muted" style={{ overflowWrap: 'anywhere' }}>{detail}</span>}
    </div>
  )
}

export function Readiness() {
  const [health, setHealth] = useState<HealthReport | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = () => {
    setBusy(true)
    setError(null)
    fetchHealth().then(setHealth).catch((e: Error) => setError(e.message)).finally(() => setBusy(false))
  }
  useEffect(refresh, [])

  const c = health?.checks
  const db = c?.database as { configured: boolean; reachable: boolean } | undefined
  const schema = c?.schema as Record<string, unknown> | undefined
  const hubspot = c?.hubspot as Record<string, boolean> | undefined
  const graph = c?.graph as Record<string, boolean> | undefined
  const hub = c?.hub as Record<string, boolean> | undefined
  const secrets = c?.secrets as Record<string, boolean> | undefined
  const access = c?.portalAccess as Record<string, string> | undefined
  const domains = c?.domains as Record<string, string | boolean | null> | undefined
  const features = c?.features
  const stageMap = c?.stageMap
  const jobs = c?.jobs
  const queues = c?.queues
  const bridge = c?.machineBridge

  return (
    <>
      <div className="page-head">
        <div>
          <h1>System status</h1>
          <p>
            Live checks — environment, feature configuration, the stage map, scheduled jobs and
            queues — plus the Mail and HubSpot probes as on-demand tools. Nothing here is
            hand-maintained; the build record lives in the spec.
          </p>
        </div>
        <div className="toolbar">
          {health && (
            <span className={`pill ${health.ok ? 'pill--ok' : 'pill--stop'}`}>
              {health.ok ? 'All checks passing' : `${health.blockers.length} blocker${health.blockers.length === 1 ? '' : 's'}`}
            </span>
          )}
          {health?.checkedAt && (
            <span className="tiny muted">checked {new Date(health.checkedAt).toLocaleTimeString('en-GB')}</span>
          )}
          <button className="btn btn--sm" disabled={busy} onClick={refresh}>{busy ? 'Checking…' : 'Refresh'}</button>
        </div>
      </div>

      {error && <div className="banner banner--stop"><strong>Health check failed.</strong><span>{error}</span></div>}
      {!health && !error && <p className="muted">Checking…</p>}

      {health && (health.blockers.length > 0 || (health.hints?.length ?? 0) > 0) && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="card__head">
            <h2>Blockers &amp; guidance</h2>
            <span className={`pill ${health.blockers.length > 0 ? 'pill--stop' : 'pill--ok'}`}>
              {health.blockers.length} blocker{health.blockers.length === 1 ? '' : 's'} · {health.hints?.length ?? 0} hint{(health.hints?.length ?? 0) === 1 ? '' : 's'}
            </span>
          </div>
          <div className="card__body">
            <ul className="small" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
              {health.blockers.map((b) => <li key={b}>⛔ {b}</li>)}
              {health.hints?.map((h) => <li key={h} className="muted">💡 {h}</li>)}
            </ul>
          </div>
        </div>
      )}

      {health && (
        <div className="split">
          <div className="card">
            <div className="card__head"><h2>Environment &amp; access</h2></div>
            <div className="card__body">
              <StatusRow ok={Boolean(db?.configured && db?.reachable)} label="Database"
                detail={db?.configured ? (db.reachable ? 'reachable' : 'configured but unreachable') : 'NETLIFY_DB_URL missing'} />
              <StatusRow ok={Boolean(schema?.migrated)} label="Schema"
                detail={schema?.migrated ? `${schema.tablesFound} tables — migrations applied` : 'migrations not applied'} />
              <StatusRow ok={Boolean(hubspot?.tokenPresent)} label="HubSpot token"
                detail={hubspot?.writesEnabled ? 'writes ENABLED' : 'writes disabled (HS_WRITE_ENABLED)'}
                warn={Boolean(hubspot?.tokenPresent) && !hubspot?.writesEnabled} />
              <StatusRow ok={Boolean(graph?.tenant && graph?.client && graph?.secret)} label="Email credentials (Graph)"
                detail={graph?.secret ? 'MS_* present' : 'sends fall back to copy-link'} />
              <StatusRow ok={Boolean(hub?.baseUrl && hub?.machineSecret)} label="Hub school search"
                detail="HUB_BASE_URL + HUB_MACHINE_SECRET" />
              <StatusRow ok={Boolean(bridge?.portalMachineSecret)} label="Customer-portal bridge"
                detail={bridge?.portalMachineSecret ? 'PORTAL_MACHINE_SECRET set' : 'PORTAL_MACHINE_SECRET missing — the customer portal cannot read quotes'} />
              <StatusRow ok={Boolean(secrets?.session && secrets?.tokenPepper)} label="Session secrets"
                detail="SESSION_SECRET + TOKEN_PEPPER" />
              <StatusRow ok={access?.wave9Sso === 'configured'} label="Wave9 SSO (Entra)"
                detail={access?.wave9Sso} warn={access?.wave9Sso !== 'configured'} />
              <StatusRow ok={Boolean(domains?.portal)} label="Domains"
                detail={`portal ${String(domains?.portal ?? '—')} · links ${String(domains?.partnerQuotes ?? 'origin')}${domains?.partnerQuotesLive === false ? ' (configured, NOT live yet)' : ''}`} />
            </div>
          </div>

          <div className="card">
            <div className="card__head">
              <h2>Feature configuration</h2>
              {features?.checked && (
                <span className={`pill ${features.features.every((f) => f.configured) ? 'pill--ok' : 'pill--warn'}`}>
                  {features.features.filter((f) => f.configured).length}/{features.features.length} configured
                </span>
              )}
            </div>
            <div className="card__body">
              <p className="small muted" style={{ marginTop: 0 }}>
                The Organisation settings that gate a feature — blank means that feature refuses politely until set.
              </p>
              {features?.checked
                ? features.features.map((f) => (
                    <StatusRow key={f.feature} ok={f.configured} warn={!f.configured} label={f.label}
                      detail={f.configured ? 'configured' : `blank: ${f.missing.join(', ')}`} />
                  ))
                : <p className="muted small">Needs the schema in place.</p>}
              <div style={{ marginTop: 10 }}>
                <StatusRow ok={Boolean(stageMap?.checked && stageMap.rows > 0 && stageMap.missing.length === 0)}
                  warn={Boolean(stageMap?.checked && stageMap.rows > 0 && stageMap.missing.length > 0)}
                  label="Pipeline stage map"
                  detail={stageMap?.checked
                    ? stageMap.rows === 0
                      ? 'EMPTY — seed from Admin → Scheduled jobs'
                      : stageMap.missing.length > 0
                        ? `missing ${stageMap.missing.join(', ')}`
                        : `${stageMap.rows} rows — every lifecycle mapped`
                    : 'unchecked'} />
              </div>
            </div>
          </div>
        </div>
      )}

      {health && (
        <div className="split">
          <div className="card">
            <div className="card__head">
              <h2>Scheduled jobs</h2>
              {jobs?.checked && (
                <span className={`pill ${jobs.jobs.some((j) => j.stale) ? 'pill--stop' : 'pill--ok'}`}>
                  {jobs.jobs.some((j) => j.stale) ? 'stalled' : 'alive'}
                </span>
              )}
            </div>
            <div className="card__body">
              <p className="small muted" style={{ marginTop: 0 }}>
                Last recorded run per job. The deal sweep runs every 10 minutes, housekeeping nightly —
                those two go red when overdue; the rest run on demand.
              </p>
              {jobs?.checked && jobs.jobs.length > 0 ? (
                <table>
                  <thead><tr><th>Job</th><th>Last run</th><th>Outcome</th></tr></thead>
                  <tbody>
                    {jobs.jobs.map((jr) => (
                      <tr key={jr.family}>
                        <td className="small">{jr.family}{jr.dryRun ? <span className="tiny muted"> · dry run</span> : null}</td>
                        <td className="small muted" title={jr.at ?? undefined}>{agoLabel(jr.ageMinutes)}</td>
                        <td>
                          {jr.stale
                            ? <span className="pill tiny pill--stop">stalled</span>
                            : jr.ok === false
                              ? <span className="pill tiny pill--warn" title={jr.error ?? undefined}>failed</span>
                              : jr.ok === true
                                ? <span className="pill tiny pill--ok">ok</span>
                                : <span className="pill tiny">running?</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="muted small">No runs recorded yet.</p>}
            </div>
          </div>

          <div className="card">
            <div className="card__head">
              <h2>Queues</h2>
              {queues?.checked && (
                <span className={`pill ${(queues.dealSync?.stuck ?? 0) > 0 ? 'pill--warn' : 'pill--ok'}`}>
                  {(queues.dealSync?.total ?? 0) + (queues.acceptancePdf?.total ?? 0) + (queues.emailStitch?.total ?? 0)} queued
                </span>
              )}
            </div>
            <div className="card__body">
              <p className="small muted" style={{ marginTop: 0 }}>
                Retrying is normal — everything here drains on the 10-minute sweep. A row that has
                retried 5+ times is failing every time, not waiting: <b>Admin → Scheduled jobs → Deal
                sync queue</b> names it and shows the stored error (decision 184).
              </p>
              {queues?.checked ? (
                <>
                  <StatusRow ok={(queues.dealSync?.stuck ?? 0) === 0} warn label="Deal sync retry queue"
                    detail={`${queues.dealSync?.total ?? 0} queued · ${queues.dealSync?.due ?? 0} due now · ${queues.dealSync?.stuck ?? 0} retried 5+ times`} />
                  <StatusRow ok={(queues.acceptancePdf?.total ?? 0) < 10} warn label="Acceptance PDF filing"
                    detail={`${queues.acceptancePdf?.total ?? 0} awaiting filing`} />
                  <StatusRow ok={(queues.emailStitch?.total ?? 0) < 25} warn label="Email stitch"
                    detail={`${queues.emailStitch?.total ?? 0} awaiting timeline stitch`} />
                </>
              ) : <p className="muted small">Needs the schema in place.</p>}
            </div>
          </div>
        </div>
      )}

      <details style={{ marginBottom: 12 }}>
        <summary className="small" style={{ cursor: 'pointer', padding: '6px 0' }}>
          <strong>Deep-dive tools</strong> — Graph mail probe (can send real test emails) and the read-only HubSpot scope probe
        </summary>
        <div className="split" style={{ marginTop: 10 }}>
          <MailPanel />
          <HubspotPanel />
        </div>
      </details>
    </>
  )
}

// ---------------------------------------------------------------- shared bits

function ProbeRow({ label, probe }: { label: string; probe: Probe }) {
  return (
    <div style={{ padding: '7px 0', borderBottom: '1px solid var(--line-soft)' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <span className={`pill tiny ${probe.ok ? 'pill--ok' : 'pill--stop'}`}>{probe.ok ? 'OK' : `FAIL${probe.status ? ` ${probe.status}` : ''}`}</span>
        <strong className="small">{label}</strong>
      </div>
      <div className="tiny muted" style={{ marginTop: 3, overflowWrap: 'anywhere' }}>{probe.detail}</div>
      {probe.hint && (
        <div className="tiny" style={{ marginTop: 4, background: 'var(--warn-bg, #fdf6e8)', border: '1px solid #f0dcb4', borderRadius: 6, padding: '5px 9px', color: '#6b5220' }}>
          💡 {probe.hint}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------- mail panel

function MailPanel() {
  const [report, setReport] = useState<MailCheckReport | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sendTo, setSendTo] = useState('')
  const [fromOverride, setFromOverride] = useState('')

  const run = async (opts: { send?: string; from?: string } = {}) => {
    setBusy(true); setError(null)
    try { setReport(await runMailCheck(opts)) } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  const events = report?.checks.portalEmailEvents
  const identities = report?.checks.identities

  return (
    <div className="card">
      <div className="card__head">
        <h2>Email (Microsoft Graph)</h2>
        {report && <span className={`pill ${report.ok ? 'pill--ok' : 'pill--stop'}`}>{report.ok ? 'healthy' : 'failing'}</span>}
      </div>
      <div className="card__body">
        <p className="small muted" style={{ marginTop: 0 }}>
          Probes the Graph credentials and the sending mailboxes, and can send real test
          emails — the raw Graph error names the fix when a send fails. Nothing is lost meanwhile:
          failed portal sends queue as events.
        </p>

        <div className="rowflex" style={{ marginBottom: 10 }}>
          <button className="btn btn--sm" disabled={busy} onClick={() => run()}>
            {busy ? 'Checking…' : 'Run checks (no email sent)'}
          </button>
        </div>

        <div className="field">
          <label>Send a real test email</label>
          <div className="rowflex">
            <input className="input" style={{ flex: 1.2 }} placeholder="to — you@wave9.co.uk" value={sendTo} onChange={(e) => setSendTo(e.target.value)} />
            <input className="input" style={{ flex: 1 }} placeholder="from (optional) — e.g. noreply@wave9.co.uk" value={fromOverride} onChange={(e) => setFromOverride(e.target.value)} />
            <button className="btn btn--sm btn--primary" disabled={busy || !sendTo.includes('@')}
              onClick={() => run({ send: sendTo.trim(), from: fromOverride.trim() || undefined })}>
              Send test
            </button>
          </div>
          <div className="hint">Leave “from” blank to test both configured identities; fill it to test a specific mailbox — the way to find out what the tenant admin actually provisioned.</div>
        </div>

        {error && <p className="pill pill--stop" style={{ whiteSpace: 'pre-wrap' }}>{error}</p>}

        {report && (
          <>
            {identities && (
              <p className="tiny muted" style={{ margin: '6px 0' }}>
                Configured senders — partner: <code>{identities.partnerQuotes}</code> · internal: <code>{identities.internalQuotes}</code> (MAIL_FROM / MAIL_FROM_INTERNAL; env change + redeploy to repoint)
              </p>
            )}
            <ProbeRow label="Credentials → Graph token" probe={report.checks.token} />
            {report.checks.mailboxes && Object.entries(report.checks.mailboxes).map(([addr, p]) => (
              <ProbeRow key={addr} label={`Mailbox ${addr}`} probe={p} />
            ))}
            {report.checks.sendTest && typeof report.checks.sendTest !== 'string'
              && Object.entries(report.checks.sendTest).map(([label, p]) => (
                <ProbeRow key={label} label={`Send test ${label}`} probe={p} />
              ))}
            {events && (
              <p className="tiny muted" style={{ marginTop: 8 }}>
                {events.note ?? <>
                  Portal sends, last 7 days — sent {events.last7Days?.email_sent ?? 0} · queued {events.last7Days?.email_queued ?? 0} · failed {events.last7Days?.email_failed ?? 0}.
                  {events.lastFailure ? <> Last failure: <span style={{ overflowWrap: 'anywhere' }}>{String(events.lastFailure.error ?? '')}</span></> : null}
                </>}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- hubspot panel

function HubspotPanel() {
  const [report, setReport] = useState<HubspotCheckReport | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = async () => {
    setBusy(true); setError(null)
    try { setReport(await runHubspotCheck()) } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  return (
    <div className="card">
      <div className="card__head">
        <h2>HubSpot (private app)</h2>
        {report && <span className={`pill ${report.ok ? 'pill--ok' : 'pill--stop'}`}>{report.ok ? 'all scopes OK' : `${report.failed.length} failing`}</span>}
      </div>
      <div className="card__body">
        <p className="small muted" style={{ marginTop: 0 }}>
          Read-only probe of every scope the portal depends on, plus the deal pipelines and their stage IDs
          (what HS_PIPELINE_* / HS_STAGE_* need) and the price book as the portal sees it. Never writes.
        </p>

        <div className="rowflex" style={{ marginBottom: 10 }}>
          <button className="btn btn--sm" disabled={busy} onClick={run}>{busy ? 'Probing…' : 'Run check'}</button>
          {report && (
            <span className="tiny muted">writes {report.writesEnabled ? 'ENABLED' : 'disabled (HS_WRITE_ENABLED)'}</span>
          )}
        </div>

        {error && <p className="pill pill--stop" style={{ whiteSpace: 'pre-wrap' }}>{error}</p>}

        {report && (
          <>
            {Object.entries(report.checks).map(([label, p]) => <ProbeRow key={label} label={label} probe={p} />)}

            {report.catalogue && (
              <p className="tiny muted" style={{ margin: '8px 0 0' }}>
                Price book: {report.catalogue.active} active of {report.catalogue.total} · {report.catalogue.recurring} recurring / {report.catalogue.oneOff} one-off· {report.catalogue.zeroPriced} at £0 (internal-only, #49) · {report.catalogue.multiLineProducts} multi-line · {report.catalogue.withAnyOverride} with overrides
                {report.catalogue.monthlyBilled.length > 0 && <> · not annual: {report.catalogue.monthlyBilled.map((m) => `${m.sku ?? m.name} (${m.frequency})`).join(', ')}</>}
              </p>
            )}

            {report.pipelines && (
              <details style={{ marginTop: 8 }}>
                <summary className="small" style={{ cursor: 'pointer' }}>Pipelines + stage IDs ({report.pipelines.length}) — for HS_PIPELINE_* / HS_STAGE_*</summary>
                {report.pipelines.map((p) => (
                  <div key={p.pipelineId} className="tiny" style={{ padding: '6px 0', borderBottom: '1px solid var(--line-soft)' }}>
                    <strong>{p.label}</strong> <code>{p.pipelineId}</code>
                    <div className="muted" style={{ marginTop: 2 }}>
                      {p.stages.map((s) => <span key={s.stageId} style={{ display: 'inline-block', marginRight: 10 }}>{s.label} <code>{s.stageId}</code></span>)}
                    </div>
                  </div>
                ))}
              </details>
            )}
          </>
        )}
      </div>
    </div>
  )
}
